import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCreateAgentChatWorkspaceExhibitMutation } from '@/services/api/agentChatWorkspaceService';
import { useGetBlobsQuery } from '@/services/api/blobService';
import type { Blob } from '@/services/types/blob-types';

import type { ExhibitSelection } from './types';

interface UseMultiExhibitSelectProps {
  matterId: string;
  workspaceId: string;
  existingExhibitBlobIds: string[];
  existingExhibitCount: number;
  onSuccess: () => void;
}

export function useMultiExhibitSelect({
  matterId,
  workspaceId,
  existingExhibitBlobIds,
  existingExhibitCount,
  onSuccess,
}: UseMultiExhibitSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selections, setSelections] = useState<ExhibitSelection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  // API hooks
  const {
    data: allBlobs = [],
    isLoading: isBlobsLoading,
    error: blobsError,
  } = useGetBlobsQuery(matterId);

  const [createExhibit] = useCreateAgentChatWorkspaceExhibitMutation();

  // Filter available blobs (PDFs and images, not already added as exhibits)
  const availableBlobs = useMemo(() => {
    const selectedIds = new Set(selections.map((s) => s.id));

    return allBlobs.filter((blob) => {
      const isPdf =
        blob.name?.toLowerCase().endsWith('.pdf') ||
        blob.file_mime_type === 'application/pdf';
      const isImage = blob.file_mime_type?.startsWith('image/') ?? false;
      const notExhibit = !existingExhibitBlobIds.includes(blob.id);
      const notSelected = !selectedIds.has(blob.id);
      return (isPdf || isImage) && notExhibit && notSelected;
    });
  }, [allBlobs, existingExhibitBlobIds, selections]);

  // Filter by search query
  const filteredBlobs = useMemo(() => {
    if (!searchQuery.trim()) return availableBlobs;
    const query = searchQuery.toLowerCase();
    return availableBlobs.filter((blob) =>
      blob.name?.toLowerCase().includes(query),
    );
  }, [availableBlobs, searchQuery]);

  // Dialog controls
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelections([]);
    setSearchQuery('');
    setPreviewBlob(null);
  }, []);

  // Selection management
  const addSelection = useCallback((blob: Blob) => {
    const newSelection: ExhibitSelection = {
      id: blob.id,
      name:
        blob.name?.replace(/\.(pdf|jpe?g|png|gif|bmp|tiff?|webp|heic)$/i, '') ??
        'Untitled',
      originalName: blob.name ?? 'Untitled',
      fileSize: blob.file_size,
    };
    setSelections((prev) => [...prev, newSelection]);
  }, []);

  const removeSelection = useCallback((id: string) => {
    setSelections((prev) => prev.filter((s) => s.id !== id));
    setPreviewBlob((prev) => (prev?.id === id ? null : prev));
  }, []);

  const updateSelectionName = useCallback((id: string, name: string) => {
    setSelections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, name } : s)),
    );
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelections([]);
    setPreviewBlob(null);
  }, []);

  // Preview
  const selectForPreview = useCallback((blob: Blob) => {
    setPreviewBlob(blob);
    // Also add to selections if not already there
    setSelections((prev) => {
      if (prev.some((s) => s.id === blob.id)) {
        return prev; // Already selected
      }
      const newSelection: ExhibitSelection = {
        id: blob.id,
        name: blob.name?.replace(/\.(pdf|jpe?g|png|gif|bmp|tiff?|webp|heic)$/i, '') ?? 'Untitled',
        originalName: blob.name ?? 'Untitled',
        fileSize: blob.file_size,
      };
      return [...prev, newSelection];
    });
  }, []);

  // Submit
  const submit = useCallback(async () => {
    if (selections.length === 0) return;

    setIsSubmitting(true);

    try {
      // Create exhibits sequentially to maintain order
      const createErrors: string[] = [];

      await selections.reduce(async (prevPromise, selection, i) => {
        await prevPromise;

        try {
          await createExhibit({
            matterId,
            workspaceId,
            data: {
              name: selection.name.trim() || selection.originalName,
              blob_id: selection.id,
              index: existingExhibitCount + i,
            },
          }).unwrap();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            `Failed to create exhibit for ${selection.name}:`,
            error,
          );
          createErrors.push(`Failed to add "${selection.name}"`);
        }
      }, Promise.resolve());

      if (createErrors.length > 0) {
        if (createErrors.length < selections.length) {
          toast.warning(
            `Added ${selections.length - createErrors.length} exhibits. ${createErrors.length} failed.`,
          );
          onSuccess();
        } else {
          toast.error('Failed to add exhibits');
        }
      } else {
        toast.success(
          `Added ${selections.length} exhibit${selections.length > 1 ? 's' : ''}`,
        );
        onSuccess();
      }

      close();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Submit error:', error);
      toast.error('Failed to add exhibits');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selections,
    createExhibit,
    matterId,
    workspaceId,
    existingExhibitCount,
    onSuccess,
    close,
  ]);

  // Computed
  const canSubmit = selections.length > 0 && !isSubmitting;

  // Helper to get blob by ID (for preview from selected items)
  const getBlobById = useCallback(
    (id: string) => allBlobs.find((b) => b.id === id) ?? null,
    [allBlobs],
  );

  return {
    // State
    isOpen,
    selections,
    searchQuery,
    isSubmitting,
    previewBlob,

    // Dialog controls
    open,
    close,

    // Search
    setSearchQuery,

    // Selection management
    addSelection,
    removeSelection,
    updateSelectionName,
    clearAllSelections,

    // Preview
    selectForPreview,
    setPreviewBlob,
    getBlobById,

    // Submit
    submit,

    // Data
    availableBlobs: filteredBlobs,
    isBlobsLoading,
    blobsError: blobsError ? 'Failed to load documents' : undefined,

    // Computed
    canSubmit,
  };
}
